import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, X, MessageSquare, AlertCircle } from "lucide-react";
import type { RichActionData } from "./helpers/richActionHelpers";
import { QuotePreviewController } from "@/modules/quotes/QuotePreviewController";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { InvoicePreviewController } from "@/modules/invoices/InvoicePreviewController";
import { ValidateDesignFilesPreviewController } from "@/modules/technicalDesigns/ValidateDesignFilesPreviewController";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveAgentTask, canUserApproveTask } from "@/lib/auth-utils";
import { UserReviewPanel } from "./UserReviewPanel";
import { EmailPreviewPanel } from "./EmailPreviewPanel";
import { AgentTaskPanel } from "./AgentTaskPanel";
import { SendEmailTaskReview } from "./SendEmailTaskReview";
import {
  isEmailSendAction,
  getRichActionDisplayName,
} from "./helpers/richActionHelpers";
import { MessageReviewPanel } from "./MessageReviewPanel";
import { CopyLinkButton } from "@/components/CopyLinkButton";
import { findPendingScreeningMessage } from "@/modules/standardAgent/components/utils";
import type { AgentSdkMessage, AgentTask } from "@forgepoint/shared";
import { AgentWorkflowRunTaskType, Permission } from "@forgepoint/shared";

interface RichActionPanelProps {
  richActionData?: RichActionData | null;
  agentTask?: AgentTask | null;
  onApprove?: (taskId: string) => void;
  onDeny?: (taskId: string) => void;
  approvingTaskId: string | null;
  denyingTaskId: string | null;
  onOpenChange?: (open: boolean) => void;
  isChatCollapsed?: boolean;
  onToggleChat?: () => void;
  isMobile?: boolean;
  sdkThreadId?: string;
  sdkMessageId?: string;
  screeningMessages?: AgentSdkMessage[];
  refreshKey?: number;
  /** True while the agent turn is running; the preview may still be changing,
   * so approval is hidden and disabled until the turn settles. */
  isThreadWorking?: boolean;
}

/**
 * Unified side panel for reviewing actions that require user approval.
 * Supports rich action previews (quotes, invoices, emails, etc.) and
 * message screening review. The panel owns the shared header chrome
 * (chat toggle, approve/deny buttons, close button).
 * @param props - The component props
 * @returns The rendered panel
 */
export const RichActionPanel: React.FC<RichActionPanelProps> = ({
  richActionData,
  agentTask,
  onApprove,
  onDeny,
  approvingTaskId,
  denyingTaskId,
  onOpenChange,
  isChatCollapsed,
  onToggleChat,
  isMobile,
  sdkThreadId,
  sdkMessageId,
  screeningMessages,
  refreshKey,
  isThreadWorking,
}) => {
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);

  // Blocks quote-level approval while an item is open in the inline editor:
  // those edits are manual-save, so approving now would finalize a stale item.
  const [inlineItemEditorOpen, setInlineItemEditorOpen] = useState(false);

  // Task actions (approve / deny / cancel) are hidden — and disabled as a
  // backstop — while the inline item editor is open (manual-save edits) or the
  // agent turn is still running (the preview may still change). Acting in
  // either state could finalize or cancel against stale content.
  const hideActions = inlineItemEditorOpen || Boolean(isThreadWorking);

  const pendingScreeningMessage = useMemo(
    () =>
      screeningMessages
        ? findPendingScreeningMessage(screeningMessages)
        : undefined,
    [screeningMessages],
  );

  const isScreeningMode = pendingScreeningMessage !== undefined;
  const isAgentTaskMode =
    !isScreeningMode && agentTask !== null && agentTask !== undefined;

  const legacyTask = isScreeningMode
    ? pendingScreeningMessage?.task
    : richActionData?.task;
  const taskId = isAgentTaskMode ? agentTask.id : legacyTask?.id;
  const taskStatus = isAgentTaskMode ? agentTask.status : legacyTask?.status;

  const canApprove = isAgentTaskMode
    ? canUserApproveAgentTask(user, agentTask, canApproveAllTasks)
    : canUserApproveTask(user, legacyTask, canApproveAllTasks);
  const requiresReview =
    taskStatus === "PENDING_REVIEW" || taskStatus === "IN_PROGRESS";
  const isApproving = approvingTaskId !== null && approvingTaskId === taskId;
  const isDenying = denyingTaskId !== null && denyingTaskId === taskId;
  const isAgentActionTask =
    isAgentTaskMode && agentTask!.type === AgentWorkflowRunTaskType.ACTION;
  const isAgentGeneralTask =
    isAgentTaskMode && agentTask!.type === AgentWorkflowRunTaskType.GENERAL;
  const approveLabel = "Approve";

  // Extract quote params from an agent action task that wraps FinalizeQuote or PreviewQuote
  const agentTaskQuoteParams = useMemo((): {
    quoteId?: string;
  } | null => {
    if (!isAgentActionTask || !agentTask?.actionParameters) {
      return null;
    }
    if (
      agentTask.actionParameters.actionId !== "FinalizeQuote" &&
      agentTask.actionParameters.actionId !== "PreviewQuote"
    ) {
      return null;
    }
    return { quoteId: agentTask.actionParameters.parameters.quoteId };
  }, [agentTask, isAgentActionTask]);

  const quoteParams = useMemo((): {
    quoteId?: string;
    quoteNumber?: number;
  } | null => {
    if (
      richActionData?.richActionType !== "finalize_quote_action" &&
      richActionData?.richActionType !== "validate_quote_action"
    ) {
      return null;
    }
    const args = richActionData.actionArguments;
    return {
      quoteId: typeof args.quoteId === "string" ? args.quoteId : undefined,
      quoteNumber:
        typeof args.quoteNumber === "number" ? args.quoteNumber : undefined,
    };
  }, [richActionData]);

  const quotePreview = useResolvedQuoteId(quoteParams);

  const invoiceParams = useMemo((): {
    invoiceId?: string;
    invoiceNumber?: number;
  } | null => {
    if (richActionData?.richActionType !== "validate_invoice_action") {
      return null;
    }
    const args = richActionData.actionArguments;
    return {
      invoiceId:
        typeof args.invoiceId === "string" ? args.invoiceId : undefined,
      invoiceNumber:
        typeof args.invoiceNumber === "number" ? args.invoiceNumber : undefined,
    };
  }, [richActionData]);

  const designFilesParams = useMemo((): {
    assetIds: string[];
    question?: string;
  } | null => {
    if (richActionData?.richActionType !== "validate_design_files_action") {
      return null;
    }
    const args = richActionData.actionArguments;
    const rawAssetIds = Array.isArray(args.assetIds) ? args.assetIds : [];
    return {
      assetIds: rawAssetIds.filter(
        (id): id is string => typeof id === "string",
      ),
      question: typeof args.question === "string" ? args.question : undefined,
    };
  }, [richActionData]);

  // Agent-created task view — render task metadata + optional action
  // preview inside the same panel shell as rich actions. General tasks get
  // a cancel-only header; action tasks with `FinalizeQuote` params get
  // the quote preview and `SendEmail` tasks the exact email, each with an
  // Approve button.
  if (isAgentTaskMode && agentTask) {
    const isTaskApproving = isApproving;
    const isTaskDenying = isDenying;
    return (
      <div className="flex flex-col h-full">
        {!inlineItemEditorOpen && (
          <div className="px-4 py-3 h-16 relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!isMobile && onToggleChat && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleChat}
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    {isChatCollapsed ? "Show Chat" : "Hide Chat"}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {requiresReview &&
                  canApprove &&
                  onApprove &&
                  onDeny &&
                  isAgentActionTask && (
                    <div className="flex gap-2">
                      {!hideActions && (
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => onApprove(agentTask.id)}
                          disabled={
                            isTaskApproving || isTaskDenying || hideActions
                          }
                        >
                          {isTaskApproving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {approveLabel}
                            </>
                          ) : (
                            approveLabel
                          )}
                        </Button>
                      )}
                      {!hideActions && (
                        <Button
                          variant="outline"
                          onClick={() => onDeny(agentTask.id)}
                          disabled={
                            isTaskApproving || isTaskDenying || hideActions
                          }
                        >
                          {isTaskDenying ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Cancel
                            </>
                          ) : (
                            <>Cancel</>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                {requiresReview &&
                  canApprove &&
                  onDeny &&
                  isAgentGeneralTask &&
                  !hideActions && (
                    <Button
                      variant="outline"
                      onClick={() => onDeny(agentTask.id)}
                      disabled={isTaskDenying || hideActions}
                    >
                      {isTaskDenying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Cancel
                        </>
                      ) : (
                        <>Cancel task</>
                      )}
                    </Button>
                  )}
                {agentTask.threadId && (
                  <CopyLinkButton
                    path={`/inbox?threadId=${agentTask.threadId}&taskId=${agentTask.id}`}
                    label={agentTask.subject ?? "(no subject)"}
                    ariaLabel="Copy link to task"
                    size="icon-sm"
                  />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Close task"
                  onClick={() => onOpenChange?.(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border" />
          </div>
        )}
        <div className="flex-1 overflow-auto scrollbar-transparent">
          {isAgentActionTask && agentTaskQuoteParams?.quoteId ? (
            <QuotePreviewController
              key={agentTaskQuoteParams.quoteId}
              quoteId={agentTaskQuoteParams.quoteId}
              refreshKey={refreshKey}
              inlineItemEditor
              onInlineItemEditorOpenChange={setInlineItemEditorOpen}
              onToggleChat={onToggleChat}
              isChatCollapsed={isChatCollapsed}
              isAgentWorking={Boolean(isThreadWorking)}
            />
          ) : isAgentActionTask &&
            agentTask.actionParameters?.actionId === "SendEmail" ? (
            <SendEmailTaskReview
              taskId={agentTask.id}
              request={agentTask.actionParameters.parameters}
            />
          ) : (
            <AgentTaskPanel task={agentTask} />
          )}
        </div>
      </div>
    );
  }

  // Error state — show failure message
  if (richActionData?.error) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 h-16 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!isMobile && onToggleChat && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onToggleChat}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  {isChatCollapsed ? "Show Chat" : "Hide Chat"}
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center px-6">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <div>
              <p className="font-medium text-sm">
                {getRichActionDisplayName(richActionData.richActionType)} Failed
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                This action was not executed.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // UserReviewPanel has its own shell with pagination controls
  if (
    richActionData &&
    richActionData.richActionType === "user_review_action"
  ) {
    return (
      <UserReviewPanel
        richActionData={richActionData}
        onApprove={onApprove}
        onDeny={onDeny}
        isApproving={isApproving}
        isDenying={isDenying}
        onOpenChange={onOpenChange}
        isChatCollapsed={isChatCollapsed}
        onToggleChat={onToggleChat}
        isMobile={isMobile}
        sdkThreadId={sdkThreadId}
        sdkMessageId={sdkMessageId}
      />
    );
  }

  // Email preview — shell with no approve/deny buttons
  if (richActionData && isEmailSendAction(richActionData.richActionType)) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 h-16 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!isMobile && onToggleChat && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onToggleChat}
                  className="gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  {isChatCollapsed ? "Show Chat" : "Hide Chat"}
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border" />
        </div>
        <div className="flex-1 overflow-auto scrollbar-transparent">
          <EmailPreviewPanel richActionData={richActionData} />
        </div>
      </div>
    );
  }

  // Screening mode or rich action preview — shared shell with approve/deny
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 h-16 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isMobile && onToggleChat && (
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleChat}
                className="gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                {isChatCollapsed ? "Show Chat" : "Hide Chat"}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {requiresReview &&
              legacyTask &&
              canApprove &&
              onApprove &&
              onDeny && (
                <div className="flex gap-2">
                  {!hideActions && (
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => onApprove(legacyTask.id)}
                      disabled={isApproving || isDenying || hideActions}
                    >
                      {isApproving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Approve
                        </>
                      ) : (
                        <>Approve</>
                      )}
                    </Button>
                  )}
                  {!hideActions && (
                    <Button
                      variant="outline"
                      onClick={() => onDeny(legacyTask.id)}
                      disabled={isApproving || isDenying || hideActions}
                    >
                      {isDenying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Cancel
                        </>
                      ) : (
                        <>Cancel</>
                      )}
                    </Button>
                  )}
                </div>
              )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange?.(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border" />
      </div>

      <div className="flex-1 overflow-auto scrollbar-transparent">
        {isScreeningMode && screeningMessages ? (
          <MessageReviewPanel messages={screeningMessages} />
        ) : (
          renderRichActionContent(
            richActionData,
            quotePreview,
            invoiceParams,
            designFilesParams,
            refreshKey,
            setInlineItemEditorOpen,
          )
        )}
      </div>
    </div>
  );
};

/**
 * Renders the appropriate preview controller for a rich action type.
 * @param richActionData - The rich action data
 * @param quotePreview - Resolved quote id state for quote actions
 * @param invoiceParams - Parsed invoice parameters
 * @param designFilesParams - Parsed design files parameters
 * @param refreshKey - Bumped to force the preview to reload
 * @param onInlineItemEditorOpenChange - Fires when the inline item editor opens/closes, so the caller can block approval
 * @returns The rendered preview content
 */
function renderRichActionContent(
  richActionData: RichActionData | null | undefined,
  quotePreview: ResolvedQuoteIdState,
  invoiceParams: { invoiceId?: string; invoiceNumber?: number } | null,
  designFilesParams: { assetIds: string[]; question?: string } | null,
  refreshKey?: number,
  onInlineItemEditorOpenChange?: (open: boolean) => void,
): React.ReactNode {
  if (!richActionData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Preview loading...</p>
      </div>
    );
  }

  if (
    richActionData.richActionType === "finalize_quote_action" ||
    richActionData.richActionType === "validate_quote_action"
  ) {
    if (quotePreview.isResolvingQuoteId) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!quotePreview.resolvedQuoteId) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3 text-center px-6">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm text-muted-foreground">Quote not found</p>
          </div>
        </div>
      );
    }
    return (
      <QuotePreviewController
        key={quotePreview.resolvedQuoteId}
        quoteId={quotePreview.resolvedQuoteId}
        refreshKey={refreshKey}
        inlineItemEditor
        onInlineItemEditorOpenChange={onInlineItemEditorOpenChange}
      />
    );
  }

  if (
    richActionData.richActionType === "validate_invoice_action" &&
    invoiceParams
  ) {
    return (
      <InvoicePreviewController
        invoiceId={invoiceParams.invoiceId}
        invoiceNumber={invoiceParams.invoiceNumber}
      />
    );
  }

  if (
    richActionData.richActionType === "validate_design_files_action" &&
    designFilesParams
  ) {
    return (
      <ValidateDesignFilesPreviewController
        assetIds={designFilesParams.assetIds}
        question={designFilesParams.question}
      />
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">Preview loading...</p>
    </div>
  );
}

/** Quote id resolution state for the rich-action quote preview. */
interface ResolvedQuoteIdState {
  resolvedQuoteId: string | null;
  isResolvingQuoteId: boolean;
}

/**
 * Resolves the quote id for a rich quote action, looking it up by quote
 * number when the agent tool call only carried a number.
 * @param quoteParams - Parsed quote params from the rich action arguments
 * @returns The resolved quote id (null when unresolved) and a resolving flag
 */
function useResolvedQuoteId(
  quoteParams: { quoteId?: string; quoteNumber?: number } | null,
): ResolvedQuoteIdState {
  const resolveQuoteIdByNumber = useRecordQuotesStore(
    (state) => state.resolveQuoteIdByNumber,
  );
  const needsLookup =
    quoteParams !== null &&
    quoteParams.quoteId === undefined &&
    quoteParams.quoteNumber !== undefined;
  const [resolvedQuoteId, setResolvedQuoteId] = useState<string | null>(
    quoteParams?.quoteId ?? null,
  );
  const [isResolvingQuoteId, setIsResolvingQuoteId] = useState(needsLookup);

  useEffect(() => {
    if (quoteParams?.quoteId !== undefined) {
      setResolvedQuoteId(quoteParams.quoteId);
      setIsResolvingQuoteId(false);
      return;
    }
    if (quoteParams?.quoteNumber === undefined) {
      setResolvedQuoteId(null);
      setIsResolvingQuoteId(false);
      return;
    }

    let cancelled = false;
    setIsResolvingQuoteId(true);
    resolveQuoteIdByNumber(quoteParams.quoteNumber).then((id) => {
      if (cancelled) {
        return;
      }
      setResolvedQuoteId(id);
      setIsResolvingQuoteId(false);
    });
    return () => {
      cancelled = true;
    };
  }, [quoteParams?.quoteId, quoteParams?.quoteNumber, resolveQuoteIdByNumber]);

  return { resolvedQuoteId, isResolvingQuoteId };
}
